import { provideHttpClient, withFetch } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';
import { GlobalErrorHandler } from '@/app/core/error-handler';
import { provideFirebase, FIREBASE_AUTH, FIREBASE_FUNCTIONS, FIREBASE_STORAGE } from '@/app/core/firebase';
import { provideIcons } from '@/app/core/icons/provider';
import { provideTheming } from '@/app/core/theming';
import { FORM_BUILDER_AUTH, FORM_BUILDER_FUNCTIONS, FORM_BUILDER_STORAGE } from '@/app/shared/form-builder';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withFetch()),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      // Every route is lazy-loaded, and a redeploy replaces the hashed chunk
      // files — an app instance loaded before the deploy then fails every
      // route's dynamic import ("Failed to fetch dynamically imported
      // module") and nav clicks appear dead. Recover by full-page loading the
      // intended URL so the browser fetches the current build.
      withNavigationErrorHandler((error) => {
        const message = String(
          (error.error as Error | undefined)?.message ?? error.error ?? ''
        );
        const isStaleChunkImport =
          /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|chunkloaderror/i.test(
            message
          );
        if (isStaleChunkImport && typeof window !== 'undefined') {
          window.location.assign(error.url);
        }
      })
    ),

    // Material
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'en-AU' },

    // Core
    provideIcons(),
    provideTheming({
      scheme: 'system',
      primary: '#14365C',
      error: '#dc2626',
    }),

    // Firebase
    provideFirebase(),
    // Form-builder file uploads use the app's Firebase Storage, and its Auth
    // for the lazy anonymous sign-in public registrants need to upload at all
    // (CLUB-1941). Admin users are always authenticated, so that branch never
    // triggers here — provided anyway to keep this file's shared-library
    // wiring in sync with the clubezy-firebase-1225 client's app.config.ts.
    { provide: FORM_BUILDER_STORAGE, useExisting: FIREBASE_STORAGE },
    { provide: FORM_BUILDER_AUTH, useExisting: FIREBASE_AUTH },
    // File-upload fields reserve a slot through a callable (CLUB-2161).
    { provide: FORM_BUILDER_FUNCTIONS, useExisting: FIREBASE_FUNCTIONS },

    // Error handling — captures unhandled exceptions app-wide
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
};
